import { useState, useMemo, useEffect, useCallback } from "react";
import { ArrowLeft, X } from "lucide-react";
import {
  ModalOverlay,
  StepIndicator,
  CountdownTimer,
  Button,
} from "@components/atoms";
import { EventInfoHeader } from "@components/molecules/EventInfoHeader";
import { ContactStep } from "@components/molecules/ContactStep";
import { PaymentStep } from "@components/molecules/PaymentStep";
import { PaymentDisplayStep } from "@components/molecules/PaymentDisplayStep";
import { PaymentSuccessModal } from "@components/molecules/PaymentSuccessModal";
import { useAuth } from "@app/providers/useAuth";
import { getCountryCodeOptions, logger, scheduleForLocalities } from "@shared/utils";
import { ticketService } from "@modules/tickets/services";
import { paymentService } from "@modules/payments/services";
import { legacyHttpClient } from "@services/http/legacyHttpClient";
import { env } from "@services/config/env";
import { confirmPurchase } from "@services/seatMap.service";
import { buildPluxData, splitPluxBase } from "@/config/plux.data";
import type { PluxData, PluxResponse } from "@/config/plux.data";
import type { Event, Locality } from "@/types";
import { CHECKOUT_TIMEOUT } from "@shared/constants";
// Analytics stubs — service not yet implemented
const trackBeginCheckout = (_: unknown) => {};
const trackAddContactInfo = (_: unknown) => {};
const trackAddPaymentInfo = (_: unknown) => {};
const trackCheckoutAbandoned = (_: unknown) => {};

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  localities: Locality[];
  selections: Record<string, number>;
  onSuccess: () => void;
  onTimeout: () => void;
  /** bug-20260421: all selected seats for NUMBERED events. */
  selectedSeats?: Array<{ seatId: number; seatLabel: string; reservedUntil: string }>;
  /** bug-20260421: session token for the seat reservation (from SeatMapModal). */
  seatSessionToken?: string;
}

/**
 * Modal de checkout, 2 pasos + la pantalla de pago.
 * 835×620 px: contacto → método de pago → pasarela. El detalle de la compra se eliminó
 * (checkout-sin-paso-detalle): repetía el resumen que ya está en la página del evento.
 */
export function CheckoutModal({
  isOpen,
  onClose,
  event,
  localities,
  selections,
  onTimeout,
  selectedSeats,
  seatSessionToken,
}: CheckoutModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);

  // Detect country code from user phone or default to Ecuador
  const detectCountryCode = useCallback(() => {
    if (!user?.phone) return "+593";
    const options = getCountryCodeOptions();
    // Try to match phone prefix
    const phoneStart = user.phone.slice(0, 3);
    const match = options.find((opt) => opt.callingCode === phoneStart);
    return match?.callingCode ?? "+593";
  }, [user?.phone]);

  // Contact fields — pre-fill from user
  const [name, setName] = useState(user?.name ?? "");
  const [cedula, setCedula] = useState(user?.cedula ?? "");
  const [email, setEmail] = useState(user?.mail ?? "");
  const [address, setAddress] = useState(user?.address ?? "");
  const [countryCode, setCountryCode] = useState(detectCountryCode());
  /**
   * Initialize phone field by removing country code prefix and leading 0.
   * This allows the user to enter just the local phone number without country prefix.
   * Example: "+593 98-345-6535" → "98345653" (Ecuador)
   */
  const [phone, setPhone] = useState(() => {
    const raw = user?.phone ?? "";
    // Remove country code prefix and leading 0 if present
    let cleaned = raw;
    const options = getCountryCodeOptions();
    for (const opt of options) {
      if (cleaned.startsWith(opt.callingCode)) {
        cleaned = cleaned.slice(opt.callingCode.length);
        break;
      }
    }
    return cleaned.startsWith("0") ? cleaned.slice(1) : cleaned;
  });

  // Payment
  const [paymentMethod, setPaymentMethod] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Payment display state
  const [orderId, setOrderId] = useState<string>("");
  const [paymentQR, setPaymentQR] = useState<string>("");
  const [deunaNumericCode, setDeunaNumericCode] = useState<string>("");
  const [deunaExpiresAt, setDeunaExpiresAt] = useState<string>("");
  const [coopercoExpiresAt, setCoopercoExpiresAt] = useState<string>("");
  const [coopercoPollUntil, setCoopercoPollUntil] = useState<string>("");
  const [paymentRedirectUrl, setPaymentRedirectUrl] = useState<string>("");
  const [paymentError, setPaymentError] = useState<string>("");
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // PayPhone SDK state
  const [payPhoneToken, setPayPhoneToken] = useState<string>("");
  const [payPhoneClientTransactionId, setPayPhoneClientTransactionId] =
    useState<string>("");
  const [payPhoneAmount, setPayPhoneAmount] = useState<number>(0);
  const [payPhoneAmountWithoutTax, setPayPhoneAmountWithoutTax] =
    useState<number>(0);
  const [payPhoneTax, setPayPhoneTax] = useState<number>(0);
  const [payPhoneAmountWithTax, setPayPhoneAmountWithTax] = useState<number>(0);
  const [payPhoneStoreId, setPayPhoneStoreId] = useState<string>("");
  const [payPhoneReference, setPayPhoneReference] = useState<string>("");

  // Plux (PagoPlux) — total confirmado por el backend (sale.total en dólares) y
  // el objeto data del botón (incluye el callback onAuthorize).
  const [pluxTotalAmount, setPluxTotalAmount] = useState<number>(0);
  const [pluxData, setPluxData] = useState<PluxData | null>(null);

  // Órdenes creadas por initializePurchase (fase 1, al continuar desde el método de pago)
  const [storedOrderIds, setStoredOrderIds] = useState<string[]>([]);

  const resetDeunaState = () => {
    setDeunaNumericCode("");
    setDeunaExpiresAt("");
  };

  // Sync user data when user loads or changes
  useEffect(() => {
    if (user) {
      setName(user.name ?? "");
      setCedula(user.cedula ?? "");
      setEmail(user.mail ?? "");
      setAddress(user.address ?? "");
      setCountryCode(detectCountryCode());

      // Clean and set phone
      const raw = user.phone ?? "";
      let cleaned = raw;
      const options = getCountryCodeOptions();
      for (const opt of options) {
        if (cleaned.startsWith(opt.callingCode)) {
          cleaned = cleaned.slice(opt.callingCode.length);
          break;
        }
      }
      setPhone(cleaned.startsWith("0") ? cleaned.slice(1) : cleaned);
    }
  }, [user, detectCountryCode]);

  const totalTickets = useMemo(
    () => Object.values(selections).reduce((s, q) => s + q, 0),
    [selections],
  );

  const totalValue = useMemo(
    () =>
      Object.entries(selections).reduce((sum, [localityId, qty]) => {
        const loc = localities.find((l) => l.id === localityId);
        return sum + (loc?.price ?? 0) * qty;
      }, 0),
    [selections, localities],
  );

  const isFree = totalValue === 0;

  // Horario a mostrar en el header del checkout: solo el/los día(s) de la(s)
  // localidad(es) elegida(s) (ej. localidad "...VIERNES..." → "Viernes: 17h00
  // - 02h00"). Si no se puede emparejar, muestra el horario completo.
  const displayHour = useMemo(() => {
    const names = Object.entries(selections)
      .filter(([, qty]) => qty > 0)
      .map(([id]) => localities.find((l) => l.id === id)?.name ?? "")
      .filter(Boolean);
    return scheduleForLocalities(event.hour ?? "", names);
  }, [selections, localities, event.hour]);

  // Cooperco: el timer del checkout dura lo mismo que la ventana de pago = pollUntil (método +
  // 5%). Al vencer, onTimeout cierra el checkout (y corta el polling), pero recién al final de
  // esa ventana, no a los 10 min. Para el resto de métodos se mantiene CHECKOUT_TIMEOUT.
  const checkoutTimerDuration = useMemo(() => {
    if (paymentMethod === "COOPERCO" && coopercoPollUntil) {
      const secs = Math.ceil(
        (new Date(coopercoPollUntil).getTime() - Date.now()) / 1000,
      );
      if (secs > 0) return secs;
    }
    return CHECKOUT_TIMEOUT;
  }, [paymentMethod, coopercoPollUntil]);

  // Fire begin_checkout once when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const localityId = Object.keys(selections)[0] ?? "";
    const loc = localities.find((l) => l.id === localityId);
    trackBeginCheckout({
      eventId: event.id,
      eventName: event.name,
      value: totalValue,
      locality: loc?.name ?? "GENERAL",
      quantity: totalTickets,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Plux: el SDK muestra los errores del pago (tarjeta rechazada, sin cupo, monto
  // mínimo, etc.) con jQuery.notify(), un plugin que NO cargamos → los errores se
  // perdían en silencio y el usuario quedaba sin feedback (a diferencia de
  // PayPhone/DeUna/JEP, que muestran paymentError). Escuchamos los mensajes del iframe
  // de Plux y reflejamos el error en la UI. El éxito llega como { code:0 } /
  // { status_payment:"paid" } y NO se toma como error.
  useEffect(() => {
    if (!isOpen || paymentMethod !== "PLUX") return;
    const onPluxMessage = (e: MessageEvent) => {
      if (typeof e.origin !== "string" || !e.origin.includes("pagoplux")) return;
      const d = e.data as
        | { error?: { message?: string }; code?: number; description?: string }
        | undefined;
      if (!d || typeof d !== "object") return;
      let msg: string | undefined;
      if (d.error?.message) msg = String(d.error.message);
      else if (typeof d.code === "number" && d.code !== 0 && d.description)
        msg = String(d.description);
      if (msg) setPaymentError(msg);
    };
    window.addEventListener("message", onPluxMessage);
    return () => window.removeEventListener("message", onPluxMessage);
  }, [isOpen, paymentMethod]);

  // Errores inline por campo — visibles al intentar continuar
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validateStep1 = () => {
    const errors: Record<string, string> = {};
    const nameParts = name.trim().split(/\s+/);
    if (!name.trim()) {
      errors.name = "El nombre completo es obligatorio";
    } else if (nameParts.length < 2) {
      errors.name = "Ingresa nombre y apellido";
    } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚàèìòùÀÈÌÒÙäëïöüÄËÏÖÜñÑ\s]+$/.test(name.trim())) {
      errors.name = "El nombre solo puede contener letras";
    }
    if (!cedula.trim()) {
      errors.cedula = "La cédula o RUC es obligatoria";
    } else if (cedula.trim().length < 10) {
      errors.cedula = "Mínimo 10 dígitos";
    }
    if (!email.trim()) {
      errors.email = "El correo electrónico es obligatorio";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Ingresa un correo válido";
    }
    if (!phone.trim()) {
      errors.phone = "El número de teléfono es obligatorio";
    } else if (phone.trim().length < 7) {
      errors.phone = "Número demasiado corto";
    }
    return errors;
  };

  const canContinue = () => {
    if (step === 1) {
      return Object.keys(validateStep1()).length === 0;
    }
    if (step === 2) return paymentMethod !== "" && termsAccepted;
    return true;
  };

  /** Wrapper de cierre que registra abandono si el flujo no completó el pago. */
  const handleClose = () => {
    if (step < 3 && !showPaymentSuccess) {
      trackCheckoutAbandoned({ eventId: event.id, eventName: event.name, stepReached: step });
    }
    onClose();
  };

  /** For free events: create sale with GRATIS code and show success directly. */
  const handleFreeCheckout = async () => {
    setIsProcessing(true);
    setErrorMessage("");
    try {
      const localityEntries = Object.entries(selections).filter(([, qty]) => qty > 0);
      if (localityEntries.length === 0) {
        setErrorMessage("Error: No se seleccionaron entradas válidas");
        return;
      }
      const nameParts = name.trim().toUpperCase().split(/\s+/);
      const firstName = nameParts[0] ?? name.toUpperCase();
      const lastName = nameParts.slice(1).join(" ") || firstName;
      try {
        await legacyHttpClient.post("/api/web/save_billing_data", {
          email,
          data: { name: firstName, uuid: cedula, phone: countryCode + phone, surname: lastName, type: "CI", email },
        });
      } catch (err) {
        logger.warn("save_billing_data failed:", err);
      }
      let lastOrderId = "";
      for (const [localityId, cantidad] of localityEntries) {
        const purchaseResponse = await ticketService.purchase({
          eventId: Number(event.id),
          localityId: Number(localityId),
          cantidad,
          formapago: "GRATIS",
          nombre: name,
          email,
          cedula,
          phone: countryCode + phone,
        });
        if (!purchaseResponse.success) {
          setErrorMessage(purchaseResponse.message || "Error al registrar entrada gratuita");
          return;
        }
        lastOrderId = String(purchaseResponse.data.orderId);
        // Confirmar la venta para que el backend genere el boleto y envíe el correo
        await paymentService.confirmSale(lastOrderId);
      }
      setOrderId(lastOrderId);
      setPaymentMethod("GRATIS");
      setShowPaymentSuccess(true);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Error al procesar el registro gratuito");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNext = async () => {
    // Step progression
    if (step === 1) {
      // Mostrar errores inline antes de avanzar
      const errors = validateStep1();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
      trackAddContactInfo({ eventId: event.id, eventName: event.name, value: totalValue });
      if (isFree) {
        // Free event: skip payment selection, create sale directly with GRATIS code
        await handleFreeCheckout();
        return;
      }
      // Contact → Payment Method
      setStep(2);
    } else if (step === 2) {
      // RF-2 (checkout-sin-paso-detalle) — las DOS fases corren encadenadas.
      //
      // Hasta este cambio había una pantalla de detalle entre medio: el paso 2 creaba las ventas
      // y traía el total del backend, y el paso 3 abría la pasarela. La pantalla se quitó porque
      // repetía el resumen que ya está en la página del evento, pero las dos fases siguen siendo
      // necesarias — lo que se eliminó es el intermedio, no el trabajo.
      //
      // `initializePurchase` deja el error en pantalla y NO avanza si falla; por eso se consulta
      // su resultado en vez de encadenar a ciegas: avanzar a la pasarela sin venta creada sería
      // mandar al comprador a pagar algo que no existe.
      trackAddPaymentInfo({ eventId: event.id, eventName: event.name, value: totalValue, paymentMethod });
      const compra = await initializePurchase();
      if (compra) {
        await handlePurchaseAndPayment(compra);
      }
    }
    // El paso 3 (pantalla de pago) no avanza más: lo resuelve la pasarela.
  };

  /**
   * Fase 1 — crea las ventas en el backend y, para PayPhone, trae el `totalpb` autoritativo.
   * Para DeUna/JEP el QR se difiere a la fase 2.
   *
   * RF-2 (checkout-sin-paso-detalle) — ya NO avanza de pantalla por su cuenta: antes terminaba
   * con `setStep(3)` para mostrar el detalle de la compra, que se eliminó. Ahora informa si la
   * compra quedó lista y quien llama decide; la pantalla la abre la fase 2.
   *
   * @returns las órdenes creadas y el total de Plux, para que la fase 2 los use sin depender de
   *   un `setState` que todavía no se aplicó. `null` cuando no hay pasarela que abrir —error, o
   *   el modo mock que ya mostró su pantalla de éxito—, con el mensaje en pantalla si
   *   corresponde.
   */
  const initializePurchase = async (): Promise<
    { orderIds: string[]; pluxTotal: number } | null
  > => {
    setIsProcessing(true);
    setErrorMessage("");
    setPaymentError("");

    try {
      const localityEntries = Object.entries(selections).filter(([, qty]) => qty > 0);
      if (localityEntries.length === 0) {
        setErrorMessage("Error: No se seleccionaron entradas válidas");
        setIsProcessing(false);
        return null;
      }

      // Save billing data
      const nameParts = name.trim().toUpperCase().split(/\s+/);
      const firstName = nameParts[0] ?? name.toUpperCase();
      const lastName = nameParts.slice(1).join(" ") || firstName;
      try {
        await legacyHttpClient.post("/api/web/save_billing_data", {
          email,
          data: { name: firstName, uuid: cedula, phone: countryCode + phone, surname: lastName, type: "CI", email },
        });
      } catch (err) {
        logger.warn("save_billing_data failed:", err);
      }

      // Create one sale per locality
      const allOrderIds: string[] = [];
      // Para eventos NUMBERED, incluir los seat IDs en el body para que el backend
      // los guarde en sale.seats y los procese al generar el ticket
      const seatsForBody = (event.seatingType === 'NUMBERED' && selectedSeats && selectedSeats.length > 0)
        ? selectedSeats.map((s) => s.seatId).join(',')
        : undefined;
      // Suma de totales confirmados por el backend — usada por Plux (base0/base12).
      let combinedPluxTotal = 0;
      for (const [localityId, cantidad] of localityEntries) {
        const purchaseResponse = await ticketService.purchase({
          eventId: Number(event.id),
          localityId: Number(localityId),
          cantidad,
          formapago: paymentMethod as "PAYPHONE" | "DEUNA" | "JEP" | "COOPERCO" | "PLUX",
          nombre: name,
          email,
          cedula,
          phone: countryCode + phone,
          seats: seatsForBody,
        });
        if (!purchaseResponse.success) {
          setErrorMessage(purchaseResponse.message || "Error al crear la venta");
          setIsProcessing(false);
          return null;
        }
        allOrderIds.push(purchaseResponse.data.orderId);
        combinedPluxTotal += purchaseResponse.data.total;
      }
      if (paymentMethod === "PLUX") {
        setPluxTotalAmount(combinedPluxTotal);
      }

      const primaryOrderId = allOrderIds[0] ?? "";
      setOrderId(primaryOrderId);
      setStoredOrderIds(allOrderIds);

      if (env.apiMode === "mock") {
        // El modo mock ya resolvió la compra acá: no hay pasarela que abrir.
        setShowPaymentSuccess(true);
        return null;
      }

      // For PayPhone: fetch totalpb per order — backend is the source of truth for amounts
      if (paymentMethod === "PAYPHONE") {
        let combinedAmountCents = 0;
        for (const oid of allOrderIds) {
          const payResp = await paymentService.preparePayphone({ orderId: oid });
          if (!payResp.success) {
            setErrorMessage(payResp.message || "Error al preparar pago PayPhone");
            setIsProcessing(false);
            return null;
          }
          combinedAmountCents += payResp.data.amount || 0;
          if (oid === primaryOrderId) {
            setPayPhoneToken(payResp.data.tokenAuth || "");
            setPayPhoneClientTransactionId(payResp.data.clientTransactionId || "");
            setPayPhoneStoreId(payResp.data.storeId || "");
            setPayPhoneReference(payResp.data.reference || "");
          }
        }
        setPayPhoneAmount(combinedAmountCents);
        setPayPhoneAmountWithoutTax(combinedAmountCents);
        setPayPhoneTax(0);
        setPayPhoneAmountWithTax(0);

        if (allOrderIds.length > 1) {
          sessionStorage.setItem("pending_extra_orders", JSON.stringify(allOrderIds.slice(1)));
        } else {
          sessionStorage.removeItem("pending_extra_orders");
        }

        // Guardar contexto de compra para trackPurchase en PaymentReturnPage
        try {
          const purchaseItems = localityEntries.map(([localityId, qty]) => {
            const loc = localities.find((l) => l.id === localityId);
            return {
              localityId,
              localityName: loc?.name ?? localityId,
              price: loc?.price ?? 0,
              comision: loc?.comision ?? 0,
              quantity: qty,
            };
          });
          const totalCents = combinedAmountCents;
          sessionStorage.setItem(
            "purchase_context",
            JSON.stringify({
              eventId: event.id,
              eventName: event.name,
              valueCents: totalCents,
              paymentMethod,
              items: purchaseItems,
            }),
          );
        } catch {
          // Analytics no debe interrumpir el flujo de pago
        }

        // Guardar contexto para confirmar asientos numerados en PaymentReturnPage
        if (event.seatingType === 'NUMBERED' && selectedSeats && selectedSeats.length > 0) {
          try {
            sessionStorage.setItem(
              'numerated_purchase_context',
              JSON.stringify({
                eventId: Number(event.id),
                localityId: Number(Object.keys(selections)[0] ?? '0'),
                seatIds: selectedSeats.map((s) => s.seatId),
                sessionToken: seatSessionToken ?? '',
                clientEmail: email,
                clientName: name,
                clientPhone: countryCode + phone,
                // Pasar la orden existente para que confirmPurchase la actualice
                // en lugar de crear una venta duplicada
                existingOrderId: primaryOrderId,
              }),
            );
          } catch {
            // sessionStorage no debe interrumpir el flujo de pago
          }
        }
      }

      // Acá terminaba con `setStep(3)` para mostrar el detalle de la compra. Esa pantalla ya no
      // existe: la fase 2 abre la pasarela y es la que avanza el paso.
      return { orderIds: allOrderIds, pluxTotal: combinedPluxTotal };
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Error al procesar el pago");
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Plux onAuthorize.succeeded handler: llama a la confirmación server-side
   * (/api/web/plux/confirm) — el backend re-verifica contra Plux y SOLO si
   * aprueba genera el QR + correo. El cliente nunca dispara la emisión
   * directamente; este callback solo confirma y refleja el resultado en la UI.
   */
  const handlePluxApproved = async (idTransaccion: string, ordenDelCobro?: string) => {
    // RF-2 (checkout-sin-paso-detalle) — la orden llega por argumento desde quien armó el pago.
    // Este callback queda capturado en `pluxData`, que ahora se construye en el mismo tick en que
    // la fase 1 creó la venta: el `storedOrderIds` del closure todavía está vacío, y confirmar
    // con una orden vacía deja el cobro de Plux sin venta a la cual asociarlo.
    const primaryOrderId = ordenDelCobro || storedOrderIds[0] || orderId;
    try {
      const confirmResult = await paymentService.confirmPlux(primaryOrderId, idTransaccion);
      if (!confirmResult.success) {
        setPaymentError(
          confirmResult.message ||
            "No pudimos confirmar tu pago con Plux. Si el dinero fue debitado, contacta a soporte.",
        );
        return;
      }

      // Confirmar los pedidos secundarios en compras multi-localidad, igual que
      // en el flujo de PayPhone: el pago combinado ya cubrió todas las órdenes.
      const extraOrderIds = storedOrderIds.slice(1);
      for (const extraOrderId of extraOrderIds) {
        await paymentService.confirmSale(extraOrderId);
      }

      // Confirmar asientos numerados (mismo patrón que DeUna/Cooperco).
      if (
        event.seatingType === "NUMBERED" &&
        selectedSeats &&
        selectedSeats.length > 0 &&
        seatSessionToken
      ) {
        try {
          await confirmPurchase({
            eventId: Number(event.id),
            localityId: Number(Object.keys(selections)[0] ?? "0"),
            seatIds: selectedSeats.map((seat) => seat.seatId),
            sessionToken: seatSessionToken,
            clientEmail: email,
            clientName: name,
            clientPhone: countryCode + phone,
            existingOrderId: primaryOrderId,
          });
        } catch (err) {
          logger.warn("confirmPurchase failed:", err);
        }
      }

      setShowPaymentSuccess(true);
    } catch (err) {
      // Defensa adicional: si confirmPlux lanza de forma inesperada (en vez de
      // resolver {success:false}), nunca se debe llegar a setShowPaymentSuccess.
      logger.warn("confirmPlux failed:", err);
      setPaymentError(
        "No pudimos confirmar tu pago con Plux. Si el dinero fue debitado, contacta a soporte.",
      );
    }
  };

  /**
   * Fase 2 — abre la pantalla de pago. Para PayPhone los datos ya quedaron en estado durante la
   * fase 1; para DeUna/JEP el QR se pide acá.
   *
   * RF-2 (checkout-sin-paso-detalle) — **recibe los datos de la fase 1 como argumento, no los lee
   * del estado.** Antes las dos fases estaban separadas por la pantalla de detalle, así que entre
   * una y otra había re-render y `storedOrderIds` / `pluxTotalAmount` ya estaban actualizados. Al
   * encadenarlas corren en el mismo tick: un `setState` de la fase 1 todavía no se ve acá, y la
   * fase 2 leería la orden y el monto VIEJOS — Plux armaría el cobro con total 0 y el resto
   * trabajaría sobre una orden que no es la recién creada.
   *
   * El estado queda como respaldo para cualquier llamada que no venga encadenada.
   */
  const handlePurchaseAndPayment = async (
    datosDeLaFase1?: { orderIds: string[]; pluxTotal: number },
  ) => {
    setIsProcessing(true);
    setErrorMessage("");
    setPaymentError("");

    try {
      const orderIds = datosDeLaFase1?.orderIds ?? storedOrderIds;
      const pluxTotal = datosDeLaFase1?.pluxTotal ?? pluxTotalAmount;
      const primaryOrderId = orderIds[0] ?? orderId;

      if (paymentMethod === "PAYPHONE") {
        setStep(3);
      } else if (paymentMethod === "PLUX") {
        // Split de montos (RF-06): con IVA, todo va a Base12; sin IVA, a Base0.
        const { base0, base12 } = splitPluxBase(pluxTotal, event.iva);
        const data = buildPluxData({
          comercioEmail: env.pluxComercioEmail,
          comercioNombre: "TicketIn",
          production: env.pluxProduction,
          clienteEmail: email,
          clienteNombre: name,
          cedula,
          telefono: countryCode + phone,
          direccion: address,
          base0,
          base12,
          // Se embebe la orden para que el webhook la reciba en `detail` y pueda emparejar la
          // venta aun si el callback client-side no registra referenciaOnline (blindaje SIN_VENTA).
          descripcion: `Entradas ${event.name} #${primaryOrderId}`,
          // Plux exige PayboxEvento (con "o" final — pedido por Plux) SOLO alfanumérico,
          // "&"→"Y", para aplicar el descuento del Banco de Guayaquil. Evento real: CHILL&CHELA
          // (id 1860). TEMPORAL: también el evento de prueba "Prueba Pago-Plux" para validar el
          // mecanismo por $1 sin cobrar los $8 (quitar el match por nombre tras validar con Plux).
          payboxEvento:
            Number(event.id) === 1860 ||
            String(event.name || "").trim().toLowerCase().includes("prueba pago")
              ? "OKTOBERFEST CHILL Y CHELA 2026"
              : undefined,
          onAuthorize: (r: PluxResponse) => {
            if (r.status === "succeeded" && r.id_transaccion) {
              void handlePluxApproved(r.id_transaccion, primaryOrderId);
            }
          },
          // Paybox manda el pago exitoso como { status_payment:"paid", detail:{id_transaccion} }
          // y el SDK lo enruta a onCatchResponse (NO a onAuthorize). Sin esto el pago se
          // completaba pero el checkout no avanzaba a "compra realizada".
          onCatchResponse: (r) => {
            const idtx = r?.detail?.id_transaccion;
            if (r?.status_payment === "paid" && idtx) {
              void handlePluxApproved(idtx, primaryOrderId);
            }
          },
        });
        setPluxData(data);
        setStep(3);
      } else if (paymentMethod === "DEUNA") {
        resetDeunaState();
        const deunaResponse = await paymentService.prepareDeUna(
          primaryOrderId,
          orderIds.length > 0 ? orderIds : undefined,
        );
        if (deunaResponse.success) {
          setPaymentQR(deunaResponse.data.qr || "");
          setPaymentRedirectUrl(deunaResponse.data.redirectUrl || "");
          setDeunaNumericCode(deunaResponse.data.numericCode || "");
          setDeunaExpiresAt(deunaResponse.data.expiresAt || "");
          setStep(3);
        } else {
          // errorMessage y no paymentError: este fallo NO avanza al paso 3, y paymentError solo
          // se pinta ahi dentro. Guardarlo ahi dejaba el boton sin hacer nada visible.
          setErrorMessage(
            deunaResponse.message || "Error al generar código DeUna",
          );
        }
      } else if (paymentMethod === "JEP") {
        const jepResponse = await paymentService.prepareJEP(primaryOrderId);
        if (jepResponse.success) {
          setPaymentQR(jepResponse.data.qr || "");
          setPaymentRedirectUrl(jepResponse.data.redirectUrl || "");
          setStep(3);
        } else {
          setErrorMessage(jepResponse.message || "Error al generar QR JEP");
        }
      } else if (paymentMethod === "COOPERCO") {
        const coopercoResponse =
          await paymentService.prepareCooperco(primaryOrderId);
        if (coopercoResponse.success) {
          setPaymentQR(coopercoResponse.data.qr || "");
          setCoopercoExpiresAt(coopercoResponse.data.expiresAt || "");
          setCoopercoPollUntil(
            coopercoResponse.data.pollUntil ||
              coopercoResponse.data.expiresAt ||
              "",
          );
          setStep(3);
        } else {
          setErrorMessage(
            coopercoResponse.message || "Error al generar QR Cooperco",
          );
        }
      }
    } catch (error) {
      // Idem: si esto salta antes del paso 3, paymentError no se ve. errorMessage se pinta
      // fuera de los pasos, asi que sirve en los tres.
      setErrorMessage(error instanceof Error ? error.message : "Error al procesar el pago");
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (
      !isOpen ||
      step !== 3 ||
      paymentMethod !== "DEUNA" ||
      !orderId ||
      showPaymentSuccess ||
      paymentError
    ) {
      return;
    }

    let isActive = true;

    const stopPolling = (intervalId: number, timeoutId?: number) => {
      window.clearInterval(intervalId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };

    const confirmNumberedSeats = async () => {
      if (
        event.seatingType !== "NUMBERED" ||
        !selectedSeats ||
        selectedSeats.length === 0 ||
        !seatSessionToken
      ) {
        return;
      }

      try {
        await confirmPurchase({
          eventId: Number(event.id),
          localityId: Number(Object.keys(selections)[0] ?? "0"),
          seatIds: selectedSeats.map((seat) => seat.seatId),
          sessionToken: seatSessionToken,
          clientEmail: email,
          clientName: name,
          clientPhone: countryCode + phone,
          existingOrderId: orderId,
        });
      } catch (err) {
        logger.warn("confirmPurchase failed:", err);
      }
    };

    const handleApproved = async () => {
      await confirmNumberedSeats();
      setShowPaymentSuccess(true);
    };

    const pollStatus = async () => {
      try {
        const result = await paymentService.consultarDeuna(orderId);
        if (!isActive) return;
        if (!result.success) {
          setPaymentError(
            result.message || "Error al consultar estado DeUna",
          );
          stopPolling(intervalId, timeoutId);
          return;
        }

        const status = result.data.status;
        if (status === "APPROVED") {
          await handleApproved();
          stopPolling(intervalId, timeoutId);
          return;
        }

        if (status === "EXPIRED") {
          setPaymentError("El código único ha expirado.");
          stopPolling(intervalId, timeoutId);
          return;
        }

        if (status === "REVERSED" || status === "FAILED") {
          setPaymentError("El pago no fue aprobado.");
          stopPolling(intervalId, timeoutId);
        }
      } catch (err) {
        if (!isActive) return;
        setPaymentError(
          err instanceof Error
            ? err.message
            : "Error al consultar estado DeUna",
        );
        stopPolling(intervalId, timeoutId);
      }
    };

    const intervalId = window.setInterval(pollStatus, 5000);
    const timeoutId = (() => {
      if (!deunaExpiresAt) return undefined;
      const expiresAtMs = new Date(deunaExpiresAt).getTime();
      if (Number.isNaN(expiresAtMs)) return undefined;
      const remainingMs = expiresAtMs - Date.now();
      if (remainingMs <= 0) return undefined;
      return window.setTimeout(() => {
        if (!isActive) return;
        setPaymentError("El código único ha expirado.");
        stopPolling(intervalId, timeoutId);
      }, remainingMs);
    })();

    pollStatus();

    return () => {
      isActive = false;
      stopPolling(intervalId, timeoutId);
    };
  }, [
    isOpen,
    step,
    paymentMethod,
    orderId,
    deunaExpiresAt,
    showPaymentSuccess,
    paymentError,
    event.seatingType,
    event.id,
    selectedSeats,
    seatSessionToken,
    selections,
    email,
    name,
    countryCode,
    phone,
  ]);

  /**
   * bug-20261001-jep-sin-confirmacion — JEP era el único método QR sin forma de enterarse de que
   * el pago entró: generaba el QR y no consultaba nada más. El comprador pagaba, el backend
   * aprobaba la venta y mandaba el boleto por correo, y la pantalla se quedaba esperando para
   * siempre.
   *
   * Parecía que lo resolvía `services/socket.service.ts`, que escucha "payment-confirmed", pero
   * ese archivo NO SE IMPORTA EN NINGÚN LADO: es código muerto, y la confirmación por socket que
   * aparentaba existir nunca estuvo conectada.
   *
   * Mismo molde que DeUna y Cooperco. Usa `getStatus`, que resuelve contra get_sale_by_id y ya
   * existía: no hizo falta tocar el backend.
   */
  useEffect(() => {
    if (
      !isOpen ||
      step !== 3 ||
      paymentMethod !== "JEP" ||
      !orderId ||
      showPaymentSuccess ||
      paymentError
    ) {
      return;
    }

    let isActive = true;

    const pollJep = async () => {
      try {
        const result = await paymentService.getStatus(orderId);
        if (!isActive) return;
        if (!result.success) return; // fallo de consulta: se reintenta, ver el catch
        const estado = result.data.status;
        if (estado === "PAGADO") {
          window.clearInterval(intervalId);
          await confirmNumberedSeatsJep();
          if (isActive) setShowPaymentSuccess(true);
          return;
        }
        // RF-6 (bug-20261001-venta-cerrada-se-lee-como-pagada) — SOLO "PAGADO" muestra el éxito.
        // Antes la condición era `=== "PAGADO"` sobre un estado que el backend devolvía mal: una
        // venta que el job de expiración cerraba a las 2 h llegaba acá como pagada y el checkout
        // mostraba la pantalla de éxito de un cobro que nunca ocurrió.
        //
        // Una venta cerrada tampoco puede dejarse en el ciclo: el QR ya no sirve y seguir
        // consultando deja al comprador esperando una confirmación imposible.
        if (estado === "EXPIRADO" || estado === "RECHAZADO" || estado === "ANULADO") {
          window.clearInterval(intervalId);
          if (isActive) {
            setPaymentError(
              estado === "EXPIRADO"
                ? "El tiempo para pagar venció y no se realizó ningún cobro. Puedes volver a comprar."
                : "El pago no se completó y no se realizó ningún cobro. Puedes intentar de nuevo.",
            );
          }
          return;
        }
        // PENDIENTE: el QR sigue vigente, seguir esperando.
      } catch (err) {
        // A propósito NO se muestra error ni se corta el ciclo: un fallo de red al consultar no
        // dice nada sobre el cobro, que ya ocurrió o no. Mostrar un error acá haría que el
        // comprador abandone una compra que sí se concretó. Se reintenta en la vuelta siguiente.
        logger.warn("Consulta de estado JEP falló, se reintenta:", err);
      }
    };

    const confirmNumberedSeatsJep = async () => {
      if (
        event.seatingType !== "NUMBERED" ||
        !selectedSeats ||
        selectedSeats.length === 0 ||
        !seatSessionToken
      ) {
        return;
      }
      try {
        await confirmPurchase({
          eventId: Number(event.id),
          localityId: Number(Object.keys(selections)[0] ?? "0"),
          seatIds: selectedSeats.map((seat) => seat.seatId),
          sessionToken: seatSessionToken,
          clientEmail: email,
          clientName: name,
          clientPhone: countryCode + phone,
          existingOrderId: orderId,
        });
      } catch (err) {
        logger.warn("confirmPurchase failed:", err);
      }
    };

    const intervalId = window.setInterval(pollJep, 5000);
    // Primera consulta inmediata: si el pago ya entró mientras se armaba el QR, no tiene sentido
    // hacer esperar 5 segundos de entrada.
    void pollJep();

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [
    isOpen,
    step,
    paymentMethod,
    orderId,
    showPaymentSuccess,
    paymentError,
    event.seatingType,
    event.id,
    selectedSeats,
    seatSessionToken,
    selections,
    email,
    name,
    countryCode,
    phone,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      step !== 3 ||
      paymentMethod !== "COOPERCO" ||
      !orderId ||
      showPaymentSuccess ||
      paymentError
    ) {
      return;
    }

    let isActive = true;

    const stopPolling = (intervalId: number, timeoutId?: number) => {
      window.clearInterval(intervalId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };

    const confirmNumberedSeats = async () => {
      if (
        event.seatingType !== "NUMBERED" ||
        !selectedSeats ||
        selectedSeats.length === 0 ||
        !seatSessionToken
      ) {
        return;
      }
      try {
        await confirmPurchase({
          eventId: Number(event.id),
          localityId: Number(Object.keys(selections)[0] ?? "0"),
          seatIds: selectedSeats.map((seat) => seat.seatId),
          sessionToken: seatSessionToken,
          clientEmail: email,
          clientName: name,
          clientPhone: countryCode + phone,
          existingOrderId: orderId,
        });
      } catch (err) {
        logger.warn("confirmPurchase failed:", err);
      }
    };

    const handleApproved = async () => {
      await confirmNumberedSeats();
      setShowPaymentSuccess(true);
    };

    const pollStatus = async () => {
      try {
        const result = await paymentService.consultarCooperco(orderId);
        if (!isActive) return;
        if (!result.success) {
          setPaymentError(
            result.message || "Error al consultar estado Cooperco",
          );
          stopPolling(intervalId, timeoutId);
          return;
        }
        if (result.data.status === "PAGADO") {
          await handleApproved();
          stopPolling(intervalId, timeoutId);
          return;
        }
        // INGRESADO: seguir esperando.
      } catch (err) {
        if (!isActive) return;
        setPaymentError(
          err instanceof Error
            ? err.message
            : "Error al consultar estado Cooperco",
        );
        stopPolling(intervalId, timeoutId);
      }
    };

    const intervalId = window.setInterval(pollStatus, 5000);
    // La ventana de pago (polling) usa pollUntil = QR + 5%: seguimos consultando un rato despues
    // de vencido el QR para alcanzar a confirmar un pago hecho sobre el final. El contador en
    // pantalla, en cambio, muestra el tiempo del QR (coopercoExpiresAt).
    const timeoutId = (() => {
      const windowEnd = coopercoPollUntil || coopercoExpiresAt;
      if (!windowEnd) return undefined;
      const expiresAtMs = new Date(windowEnd).getTime();
      if (Number.isNaN(expiresAtMs)) return undefined;
      const remainingMs = expiresAtMs - Date.now();
      if (remainingMs <= 0) return undefined;
      return window.setTimeout(() => {
        if (!isActive) return;
        setPaymentError("El QR expiró. Genera uno nuevo para volver a intentar.");
        stopPolling(intervalId, timeoutId);
      }, remainingMs);
    })();

    pollStatus();

    return () => {
      isActive = false;
      stopPolling(intervalId, timeoutId);
    };
  }, [
    isOpen,
    step,
    paymentMethod,
    orderId,
    coopercoExpiresAt,
    coopercoPollUntil,
    showPaymentSuccess,
    paymentError,
    event.seatingType,
    event.id,
    selectedSeats,
    seatSessionToken,
    selections,
    email,
    name,
    countryCode,
    phone,
  ]);

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    else onClose();
  };

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleClose}>
      <div
        className="flex flex-col rounded-2xl bg-white px-4 sm:px-8 py-6 overflow-y-auto w-full max-w-[835px]"
        style={{ maxHeight: "90vh" }}
      >
        {/* Top bar: back arrow, steps, close */}
        <div className="flex items-center justify-between mb-1">
          <button
            className="flex cursor-pointer items-center justify-center border-none bg-transparent text-black"
            onClick={handleBack}
            aria-label="Atrás"
          >
            <ArrowLeft size={24} />
          </button>

          <StepIndicator currentStep={step} totalSteps={2} />

          <button
            className="flex cursor-pointer items-center justify-center border-none bg-transparent text-black"
            onClick={handleClose}
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        {/* Checkout title + countdown */}
        <div className="flex items-center justify-between mb-4">
          <span className="font-semibold text-black" style={{ fontSize: 24 }}>
            Checkout
          </span>
          <CountdownTimer
            duration={checkoutTimerDuration}
            onTimeout={onTimeout}
          />
        </div>

        {/* Event info header */}
        <div className="mb-4">
          <EventInfoHeader event={event} totalTickets={totalTickets} hour={displayHour} />
        </div>

        {/* Step content */}
        <div className="flex-1 mb-5">
          {step === 1 && (
            <ContactStep
              name={name}
              cedula={cedula}
              email={email}
              address={address}
              countryCode={countryCode}
              phone={phone}
              onNameChange={(v) => { setName(v); setFieldErrors((e) => ({ ...e, name: "" })); }}
              onCedulaChange={(v) => { setCedula(v); setFieldErrors((e) => ({ ...e, cedula: "" })); }}
              onEmailChange={(v) => { setEmail(v); setFieldErrors((e) => ({ ...e, email: "" })); }}
              onAddressChange={setAddress}
              onCountryCodeChange={setCountryCode}
              onPhoneChange={(v) => { setPhone(v); setFieldErrors((e) => ({ ...e, phone: "" })); }}
              fieldErrors={fieldErrors}
            />
          )}
          {step === 2 && (
            <PaymentStep
              event={event}
              selectedMethod={paymentMethod}
              onMethodChange={setPaymentMethod}
              termsAccepted={termsAccepted}
              onTermsChange={setTermsAccepted}
            />
          )}
          {step === 3 && (
            <PaymentDisplayStep
              paymentMethod={paymentMethod as "PAYPHONE" | "DEUNA" | "JEP" | "COOPERCO" | "PLUX"}
              qrData={paymentQR}
              numericCode={deunaNumericCode}
              expiresAt={(paymentMethod === "COOPERCO" ? coopercoExpiresAt : deunaExpiresAt) || undefined}
              redirectUrl={paymentRedirectUrl}
              error={paymentError}
              isLoading={isProcessing}
              payPhoneToken={payPhoneToken}
              payPhoneClientTransactionId={payPhoneClientTransactionId}
              payPhoneAmount={payPhoneAmount}
              payPhoneAmountWithoutTax={payPhoneAmountWithoutTax}
              payPhoneAmountWithTax={payPhoneAmountWithTax}
              payPhoneTax={payPhoneTax}
              payPhoneStoreId={payPhoneStoreId}
              payPhoneReference={payPhoneReference}
              pluxData={pluxData}
            />
          )}
        </div>

        {/* Error message */}
        {errorMessage && (
          <div className="mb-4 rounded-lg bg-red-100 px-4 py-3 text-red-700 text-sm">
            {errorMessage}
          </div>
        )}

        {/* Bottom action — only for steps 1, 2 & 3 */}
        {step < 3 && (
          <div className="flex justify-center">
            <Button
              className="w-full max-w-[340px] h-[50px] bg-secondary-120 text-primary-110 hover:bg-secondary-120 hover:opacity-90 disabled:opacity-40"
              disabled={!canContinue() || isProcessing}
              onClick={handleNext}
            >
              {isProcessing ? "Procesando..." : "Continuar"}
            </Button>
          </div>
        )}

        {/* Payment Success Modal */}
        {showPaymentSuccess && (
          <PaymentSuccessModal
            isOpen={showPaymentSuccess}
            orderId={orderId}
            paymentMethod={paymentMethod}
            onClose={onClose}
            onViewTickets={() => {
              onClose(); // mock success — no abandono
              // Navigate to my tickets page
              window.location.hash = "#/tickets";
            }}
          />
        )}
      </div>
    </ModalOverlay>
  );
}